
import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { BookOpen, Users, TrendingUp, MessageSquare, Lightbulb, ArrowRightCircle, Award, GraduationCap, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

const ForSchoolsPage = () => {
  const fadeIn = (delay = 0, y = 20) => ({
    hidden: { opacity: 0, y: y, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.7, delay, ease: [0.6, -0.05, 0.01, 0.99] } },
  });

  const benefits = [
    {
      icon: <MessageSquare size={36} className="text-cm-pink" />,
      title: "Versterk Interne Begeleiding",
      description: "Classbuddy biedt een laagdrempelig luisterend oor en vult het werk van IB'ers en zorgcoördinatoren aan door vroegsignalering van behoeften."
    },
    {
      icon: <Users size={36} className="text-cm-yellow" />,
      title: "Bevorder Emotioneel Welzijn",
      description: "Kinderen kunnen anoniem en veilig praten, wat bijdraagt aan een positiever klasklimaat en de algehele emotionele gezondheid."
    },
    {
      icon: <TrendingUp size={36} className="text-cm-green" />,
      title: "Data-Gedreven Inzichten",
      description: "Ontvang waardevolle, geanonimiseerde data over sociaal-emotionele thema's, voor een proactief en gericht schoolbeleid."
    },
     {
      icon: <Lightbulb size={36} className="text-cm-blue" />,
      title: "Preventie & Vroegsignalering",
      description: "Identificeer trends en behoeften vroegtijdig, waardoor scholen preventief kunnen handelen en gerichte ondersteuning kunnen bieden."
    }
  ];

  const trainings = [
    {
      buddy: "Nouri",
      theme: "Vriendschappen & Groepsvorming",
      training: "Leer sociale vaardigheden en bevorder inclusie, geïnspireerd door Nouri's expertise in vriendschap.",
      image: "https://storage.googleapis.com/hostinger-horizons-assets-prod/e926ce51-6e11-4ab9-9fdf-60980d54863c/36bcc828609374cccfea37437b0087b4.png",
      gradientFrom: "from-blue-400",
      gradientTo: "to-blue-600"
    },
    {
      buddy: "Luma",
      theme: "Angst & Spanning",
      training: "Ontdek hoe je leerlingen helpt omgaan met spanning en stress, met Luma's kalmerende methoden.",
      image: "https://storage.googleapis.com/hostinger-horizons-assets-prod/e926ce51-6e11-4ab9-9fdf-60980d54863c/64410c4a8456aa01d48bf1fcccc62851.png",
      gradientFrom: "from-yellow-400",
      gradientTo: "to-yellow-600"
    },
    {
      buddy: "Rafi",
      theme: "Zelfvertrouwen & Keuzes",
      training: "Versterk leerlingcoaching en motivatie, gebaseerd op Rafi's aanpak voor zelfvertrouwen en keuzes.",
      image: "https://storage.googleapis.com/hostinger-horizons-assets-prod/e926ce51-6e11-4ab9-9fdf-60980d54863c/e0628690ae18b65afa2ea44c952b4171.png",
      gradientFrom: "from-purple-400",
      gradientTo: "to-purple-600"
    },
    {
      buddy: "Zena",
      theme: "Emotieregulatie",
      training: "Beheers emotieherkenning en gespreksvoering, geïnspireerd door Zena's luisterend oor.",
      image: "https://storage.googleapis.com/hostinger-horizons-assets-prod/e926ce51-6e11-4ab9-9fdf-60980d54863c/5be273ac8daf84ec650dbc720f5bc36f.png",
      gradientFrom: "from-pink-400",
      gradientTo: "to-pink-600"
    },
    {
      buddy: "Appie",
      theme: "Pesten & Weerbaarheid",
      training: "Focus op signalering en preventie van pesten, met Appie's methoden voor weerbaarheid.",
      image: "https://storage.googleapis.com/hostinger-horizons-assets-prod/e926ce51-6e11-4ab9-9fdf-60980d54863c/32a7bab3949898c8b91390aea3b3cebc.png",
      gradientFrom: "from-green-400",
      gradientTo: "to-green-600"
    },
    {
      buddy: "Chloe",
      theme: "Thuissituatie & Veranderingen",
      training: "Begeleid kinderen bij thuissituaties en veranderingen, met Chloe's begripvolle aanpak.",
      image: "https://storage.googleapis.com/hostinger-horizons-assets-prod/e926ce51-6e11-4ab9-9fdf-60980d54863c/6fa70f008d66b5c3004d0717a1bb1874.png",
      gradientFrom: "from-orange-400",
      gradientTo: "to-orange-600"
    }
  ];

  return (
    <div className="container mx-auto px-4 section-padding overflow-hidden">
      <motion.div
        initial="hidden"
        animate="visible"
        className="text-center mb-16"
      >
        <motion.div variants={fadeIn(0,30)} className="flex justify-center mb-8">
          <BookOpen size={72} className="text-cm-blue" />
        </motion.div>
        <motion.h1
          variants={fadeIn(0.1, 30)}
          className="page-title"
        >
          Minder ruis, meer ruimte om <span className="text-gradient-pink-blue font-medium">écht les te geven.</span>
        </motion.h1>
        <motion.p
          variants={fadeIn(0.2, 20)}
          className="page-subtitle"
        >
          Classbuddy is dé innovatieve tool die basisscholen ondersteunt in het bevorderen van sociaal-emotioneel welzijn. Creëer een omgeving waarin elk kind floreert en leerkrachten zich kunnen focussen op hun kerntaak: <strong className="font-semibold">inspirerend onderwijs</strong>.
        </motion.p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-16">
        {benefits.map((benefit, index) => (
          <motion.div
            key={index}
            variants={fadeIn(0.3 + index * 0.1, 20)}
            initial="hidden"
            animate="visible"
            className="glassmorphism-card p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col h-full"
            whileHover={{ y: -10, scale: 1.02 }}
          >
            <div className="flex items-center mb-5">
               <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-cm-blue-light to-white rounded-full flex items-center justify-center shadow-md mr-4">
                {benefit.icon}
              </div>
              <h2 className="text-2xl font-heading font-medium text-cm-blue-dark">{benefit.title}</h2>
            </div>
            <p className="text-cm-gray text-md leading-relaxed flex-grow">{benefit.description}</p>
          </motion.div>
        ))}
      </div>
      
      <motion.div 
        variants={fadeIn(0.8, 20)}
        initial="hidden"
        animate="visible"
        className="text-center p-10 bg-gradient-to-r from-cm-blue-light/70 via-cm-yellow-light/70 to-cm-pink-light/70 rounded-3xl shadow-2xl"
      >
        <img  
          alt="Docent kijkt naar het Classbuddy dashboard op een laptop, met grafieken en buddy iconen"
          className="w-full max-w-md mx-auto rounded-2xl shadow-xl mb-10 object-cover"
          src="https://storage.googleapis.com/hostinger-horizons-assets-prod/e926ce51-6e11-4ab9-9fdf-60980d54863c/58cbc8adad78f452db13719c09d23e7f.png" />
        <h2 className="text-4xl font-heading font-medium text-cm-blue-dark mb-5">Klaar om het verschil te maken?</h2>
        <p className="text-lg text-cm-gray max-w-2xl mx-auto mb-10 leading-relaxed">
          Vraag een vrijblijvende demo aan en ontdek persoonlijk hoe Classbuddy uw school kan transformeren. Samen bouwen we aan een positief, veilig en ondersteunend leerklimaat voor elke leerling.
        </p>
        <Button asChild size="lg" className="bg-gradient-to-r from-cm-pink to-cm-blue hover:from-cm-pink-dark hover:to-cm-blue-dark text-white font-medium text-lg px-10 py-7 rounded-xl shadow-xl transform hover:scale-105 transition-all duration-300 group">
          <Link to="/contact?subject=DemoAanvraag">
            Vraag een Demo aan <ArrowRightCircle className="ml-2.5 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Button>
      </motion.div>

      <motion.div variants={fadeIn(1.0, 20)} initial="hidden" animate="visible" className="mt-20 mb-20 p-10 glassmorphism-card rounded-2xl text-center">
        <Award size={48} className="text-cm-yellow mx-auto mb-6" />
        <h3 className="text-3xl font-heading font-medium text-cm-blue-dark mb-4">Partner in Welzijn</h3>
        <p className="text-lg text-cm-gray max-w-2xl mx-auto leading-relaxed">
          Classbuddy is meer dan een tool; het is een <strong className="font-semibold">partnerschap</strong>. Wij bieden doorlopende ondersteuning, training en delen inzichten om samen met u het welzijn op school naar een hoger niveau te tillen. Uw succes is ons succes.
        </p>
      </motion.div>
    </div>
  );
};

export default ForSchoolsPage;
